import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { DEFAULT_CATALOG } from "../../shared/catalog";
import { formatMxn } from "../../shared/metrics";
import { CATEGORY_LABELS } from "../../shared/model";
import { Brand, StatusBadge } from "../components/Brand";
import { CategoryBars, Histogram, ServiceRanking } from "../components/Charts";
import { ErrorCard, Loading } from "../components/Loading";
import { useLiveMetrics } from "../hooks/useLiveMetrics";

const scenes = [
  ["resumen", "La factura invisible"],
  ["centro", "Media vs. mediana"],
  ["distribucion", "¿Dónde está el grupo?"],
  ["servicios", "La carrera de servicios"],
  ["categorias", "¿En qué se nos va?"],
  ["servidor", "Mi servidor en casa"],
] as const;

function responseLabel(count: number) {
  return `${count} ${count === 1 ? "RESPUESTA" : "RESPUESTAS"}`;
}

function AnimatedMetric({
  value,
  format,
}: {
  value: number;
  format: "decimal" | "integer" | "mxn" | "percent";
}) {
  const [displayed, setDisplayed] = useState(value);
  const displayedRef = useRef(value);

  useEffect(() => {
    const startValue = displayedRef.current;
    if (
      startValue === value ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      displayedRef.current = value;
      setDisplayed(value);
      return;
    }

    let frame = 0;
    const startedAt = performance.now();
    const duration = 650;
    const tick = (now: number) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      const nextValue = startValue + (value - startValue) * eased;
      displayedRef.current = nextValue;
      setDisplayed(nextValue);
      if (progress < 1) frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [value]);

  if (format === "mxn") return formatMxn(Math.round(displayed));
  if (format === "percent") return `${Math.round(displayed)}%`;
  if (format === "decimal") {
    return new Intl.NumberFormat("es-MX", { maximumFractionDigits: 1 }).format(displayed);
  }
  return new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(displayed);
}

function PlaybackIcon({ frozen }: { frozen: boolean }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 32 32">
      {frozen ? (
        <path d="M9 6v20l17-10L9 6Z" />
      ) : (
        <>
          <rect height="20" width="6" x="7" y="6" />
          <rect height="20" width="6" x="19" y="6" />
        </>
      )}
    </svg>
  );
}

export function LiveDashboard({ slug, demo = false }: { slug: string; demo?: boolean }) {
  const [frozen, setFrozen] = useState(false);
  const { metrics, connection, error, pulse } = useLiveMetrics(
    slug,
    demo ? DEFAULT_CATALOG : undefined,
    frozen,
  );
  const [currentScene, setCurrentScene] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const active = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!active) return;
        const index = scenes.findIndex(([id]) => id === active.target.id);
        if (index >= 0) setCurrentScene(index);
      },
      { root: document.querySelector(".live-dashboard"), threshold: [0.45, 0.7] },
    );
    scenes.forEach(([id]) => {
      const node = document.getElementById(id);
      if (node) observer.observe(node);
    });
    return () => observer.disconnect();
  }, [metrics]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        ![
          "ArrowDown",
          "ArrowRight",
          "PageDown",
          "ArrowUp",
          "ArrowLeft",
          "PageUp",
        ].includes(event.key)
      ) {
        return;
      }
      const target = event.target;
      if (target instanceof HTMLButtonElement || target instanceof HTMLInputElement) return;
      event.preventDefault();
      const direction = ["ArrowDown", "ArrowRight", "PageDown"].includes(event.key) ? 1 : -1;
      const next = Math.min(Math.max(currentScene + direction, 0), scenes.length - 1);
      document
        .getElementById(scenes[next]?.[0] ?? "resumen")
        ?.scrollIntoView({ behavior: "smooth" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentScene]);

  const topCategory = useMemo(
    () =>
      [...(metrics?.categories ?? [])].sort(
        (a, b) => b.monthlySpendCents - a.monthlySpendCents,
      )[0],
    [metrics],
  );

  if (!metrics && error) {
    return (
      <main className="center-page" id="main">
        <ErrorCard message="No encontramos el dashboard de esta sesión." />
      </main>
    );
  }
  if (!metrics) return <Loading label="Conectando con el auditorio…" />;

  const rawGap =
    metrics.medianMonthlyCents === 0
      ? metrics.meanMonthlyCents === 0
        ? 0
        : 100
      : Math.round(
          ((metrics.meanMonthlyCents - metrics.medianMonthlyCents) /
            metrics.medianMonthlyCents) *
            100,
        );
  const meanMedianGap = Math.abs(rawGap);
  const comparisonMax = Math.max(
    metrics.meanMonthlyCents,
    metrics.medianMonthlyCents,
    1,
  );
  const statusText = frozen
    ? "❚❚ CONGELADO"
    : demo
      ? "● DEMO"
      : connection === "live"
        ? "● EN VIVO"
        : "↻ RECONECTANDO";
  const statusTone = frozen
    ? "yellow"
    : demo
      ? "purple"
      : connection === "live"
        ? "green"
        : "yellow";

  return (
    <main
      className={`live-dashboard live-pulse-${pulse % 2}`}
      id="main"
      tabIndex={-1}
    >
      <div className="live-brand-lockup">
        <Brand club />
      </div>
      <div className="live-status" aria-live="polite">
        <StatusBadge tone={statusTone}>
          {statusText} · {responseLabel(metrics.responseCount)}
        </StatusBadge>
      </div>

      <nav className="scene-nav" aria-label="Seis escenas del dashboard">
        {scenes.map(([id, label], index) => (
          <a
            aria-current={currentScene === index ? "step" : undefined}
            href={`#${id}`}
            key={id}
            title={`${String(index + 1).padStart(2, "0")} · ${label}`}
          >
            <span>{String(index + 1).padStart(2, "0")}</span>
          </a>
        ))}
      </nav>

      <button
        aria-label={frozen ? "Reanudar resultados en vivo" : "Congelar resultados en vivo"}
        aria-pressed={frozen}
        className={`freeze-control ${frozen ? "is-frozen" : ""}`}
        onClick={() => setFrozen((value) => !value)}
        title={frozen ? "Reanudar resultados" : "Congelar resultados"}
        type="button"
      >
        <PlaybackIcon frozen={frozen} />
      </button>

      <section
        className={`dashboard-scene scene-hero ${currentScene === 0 ? "is-active" : ""}`}
        id="resumen"
      >
        <div className="scene-kicker">01 · LA FACTURA INVISIBLE</div>
        <h1>
          Este auditorio gasta
          <strong className="live-metric">
            <AnimatedMetric format="mxn" value={metrics.totalMonthlyCents} />
          </strong>
          <span>cada mes</span>
        </h1>
        <div className="hero-stats">
          <article className="hero-stat-year">
            <small>En un año</small>
            <strong className="live-metric">
              <AnimatedMetric format="mxn" value={metrics.totalAnnualCents} />
            </strong>
          </article>
          <article>
            <small>Respuestas</small>
            <strong className="live-metric">
              <AnimatedMetric format="integer" value={metrics.responseCount} />
            </strong>
          </article>
          <article>
            <small>Servicios por persona</small>
            <strong className="live-metric">
              <AnimatedMetric format="decimal" value={metrics.averageServiceCount} />
            </strong>
          </article>
        </div>
        <p className="scene-question">¿Te sorprendió más tu total o el total del auditorio?</p>
      </section>

      <section
        className={`dashboard-scene comparison-scene ${currentScene === 1 ? "is-active" : ""}`}
        id="centro"
      >
        <div className="comparison-copy">
          <div className="scene-kicker">02 · ¿CUÁNTO ES “NORMAL”?</div>
          <h2>Media vs. mediana</h2>
          <p className="scene-lead">
            La media suma todo y reparte. La mediana encuentra a la persona que queda justo en
            medio.
          </p>
          <div className="insight-card">
            <span>✦</span>
            <p>
              {metrics.responseCount === 0
                ? "Las torres crecerán sobre la misma escala conforme lleguen respuestas."
                : metrics.medianMonthlyCents === 0 && metrics.meanMonthlyCents > 0
                  ? "La mediana sigue en $0: al menos la mitad del grupo no marcó una suscripción."
                  : meanMedianGap > 10
                    ? `La media está ${meanMedianGap}% ${
                        rawGap >= 0 ? "arriba" : "abajo"
                      } de la mediana: los extremos mueven el promedio.`
                    : "Ambas cifras están cerca: el gasto del grupo se distribuye de forma bastante pareja."}
            </p>
          </div>
        </div>
        <div
          className="metric-towers"
          role="img"
          aria-label={`Media ${formatMxn(metrics.meanMonthlyCents)}; mediana ${formatMxn(
            metrics.medianMonthlyCents,
          )}`}
        >
          <div className="difference-label">
            <strong className="live-metric">
              <AnimatedMetric format="percent" value={meanMedianGap} />
            </strong>
            <span>de diferencia</span>
          </div>
          {[
            ["Media", metrics.meanMonthlyCents, "promedio mensual"],
            ["Mediana", metrics.medianMonthlyCents, "centro del grupo"],
          ].map(([label, value, description]) => {
            const numericValue = Number(value);
            const height =
              numericValue === 0 ? 0 : Math.max((numericValue / comparisonMax) * 100, 4);
            return (
              <article className={`metric-tower metric-tower--${String(label).toLowerCase()}`} key={label}>
                <strong className="live-metric">
                  <AnimatedMetric format="mxn" value={numericValue} />
                </strong>
                <div className="metric-tower-track">
                  <span
                    style={{ "--tower-height": `${height}%` } as CSSProperties}
                  />
                </div>
                <div>
                  <b>{label}</b>
                  <small>{description}</small>
                </div>
              </article>
            );
          })}
          <span className="tower-baseline" aria-hidden="true" />
        </div>
      </section>

      <section
        className={`dashboard-scene dark-scene distribution-scene ${
          currentScene === 2 ? "is-active" : ""
        }`}
        id="distribucion"
      >
        <div className="scene-heading-row">
          <div>
            <div className="scene-kicker">03 · NO TODOS GASTAMOS IGUAL</div>
            <h2>¿Dónde está el grupo?</h2>
          </div>
          <p className="scene-lead">
            Cada edificio es un rango. Sólo mostramos datos agregados.
          </p>
        </div>
        <Histogram
          buckets={metrics.histogram}
          meanMonthlyCents={metrics.meanMonthlyCents}
          medianMonthlyCents={metrics.medianMonthlyCents}
          responseCount={metrics.responseCount}
        />
      </section>

      <section
        className={`dashboard-scene services-scene ${currentScene === 3 ? "is-active" : ""}`}
        id="servicios"
      >
        <div className="scene-grid ranking-scene">
          <div>
            <div className="scene-kicker">04 · LA CARRERA DE SERVICIOS</div>
            <h2>¿Qué domina la nube?</h2>
            <p className="scene-lead">
              Cambia entre popularidad y gasto acumulado. Son dos historias distintas.
            </p>
          </div>
          <ServiceRanking services={metrics.services} />
        </div>
      </section>

      <section
        className={`dashboard-scene category-scene ${currentScene === 4 ? "is-active" : ""}`}
        id="categorias"
      >
        <div className="scene-grid category-scene-grid">
          <div>
            <div className="scene-kicker">05 · ¿EN QUÉ SE NOS VA?</div>
            <h2>{topCategory ? CATEGORY_LABELS[topCategory.category] : "Sin respuestas aún"}</h2>
            <p className="scene-lead">
              {topCategory && topCategory.monthlySpendCents > 0
                ? `Lidera con ${formatMxn(
                    topCategory.monthlySpendCents,
                  )} al mes entre todo el grupo.`
                : "Los carriles se activarán conforme lleguen respuestas."}
            </p>
          </div>
          <CategoryBars categories={metrics.categories} />
        </div>
      </section>

      <section
        className={`dashboard-scene server-scene ${currentScene === 5 ? "is-active" : ""}`}
        id="servidor"
      >
        <div className="server-copy">
          <div className="scene-kicker">06 · OTRA POSIBILIDAD</div>
          <h2>¿Y si la nube viviera en tu casa?</h2>
          <div className="server-closing-row">
            <div className="server-thesis">
              <strong>La pregunta no es sólo cuánto cuesta.</strong>
              <span>
                Una PC en casa puede convertirse en infraestructura propia: más control,
                aprendizaje y autonomía.
              </span>
            </div>
            <blockquote>“Un gran poder, conlleva una gran responsabilidad.”</blockquote>
          </div>
        </div>
        <div className="spider-watermark" aria-hidden="true">
          <img alt="" src="/spiderman-symbol.png" />
        </div>
      </section>
    </main>
  );
}
